import { Injectable } from '@angular/core';
import { BundleInfo, CapacitorUpdater, DownloadEvent } from '@capgo/capacitor-updater';
import { Capacitor } from '@capacitor/core';
import { ModalController, ToastController } from '@ionic/angular';
import { concat, interval, Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { LiveUpdateModalComponent } from '../../modals/live-update-modal/live-update-modal.component';

export interface UpdateEvent {
  type: string;
}

export interface UpdateProgressEvent extends UpdateEvent {
  type: 'progress';
  progress: number;
}

export interface UpdateCompleteEvent extends UpdateEvent {
  type: 'complete';
  success: boolean;
}

export type UpdateEvents = UpdateProgressEvent | UpdateCompleteEvent;

interface LiveUpdateManifest {
  version: string;
  url: string;
  checksum?: string;
  sessionKey?: string;
}

export const mockDownload = (): Observable<UpdateEvents> =>
  concat(
    interval(10).pipe(
      take(101),
      map((progress): UpdateProgressEvent => ({ type: 'progress', progress: progress / 100 })),
    ),
    of<UpdateCompleteEvent>({ type: 'complete', success: true }),
  );

@Injectable()
export class LiveUpdateService {
  private availableUpdate?: LiveUpdateManifest;
  private downloadedBundle?: BundleInfo;

  constructor(
    private toasts: ToastController,
    private modals: ModalController,
  ) {}

  async hasUpdate(): Promise<boolean> {
    if (!Capacitor.isNativePlatform() || !environment.liveUpdateManifestUrl) {
      return true;
    }

    const response = await fetch(environment.liveUpdateManifestUrl, { cache: 'no-store' });
    if (!response.ok) {
      throw new Error(`Live update manifest request failed: ${response.status}`);
    }

    const manifest = (await response.json()) as LiveUpdateManifest;
    if (!manifest.version || !manifest.url) {
      throw new Error('Live update manifest must include version and url.');
    }

    const current = await CapacitorUpdater.current();
    console.log('Live update version check', {
      currentBundleId: current.bundle.id,
      currentBundleVersion: current.bundle.version,
      manifestVersion: manifest.version,
    });
    const updateAvailable = current.bundle.version !== manifest.version;
    this.availableUpdate = updateAvailable ? manifest : undefined;
    return updateAvailable;
  }

  async toastAvailableUpdate() {
    const toast = await this.toasts.create({
      header: 'New Update Available',
      message: 'Tap to start the download.',
      buttons: [
        {
          icon: 'cloud-download-outline',
        },
      ],
      cssClass: 'download-toast',
      color: 'dark',
      position: 'bottom',
    });
    await toast.present();
    toast.onclick = () => toast.dismiss();
    return toast.onDidDismiss();
  }

  async showUpdateProgress() {
    const modal = await this.modals.create({
      component: LiveUpdateModalComponent,
      cssClass: '', //TODO: make this black with white status and logo
      backdropDismiss: false,
    });
    await modal.present();
    // return popover.onDidDismiss();
  }

  applyUpdate() {
    if (!this.downloadedBundle) {
      return Promise.resolve();
    }
    return CapacitorUpdater.set({ id: this.downloadedBundle.id });
  }

  downloadLiveUpdate(): Observable<UpdateEvents> {
    if (!Capacitor.isNativePlatform()) {
      return mockDownload();
    }
    return new Observable<UpdateEvents>(subscriber => {
      subscriber.next({ type: 'progress', progress: 0.01 });
      const update = this.availableUpdate;
      if (!update) {
        subscriber.next({ type: 'complete', success: false });
        subscriber.complete();
        return;
      }

      let removeListener: (() => void) | undefined;
      CapacitorUpdater.addListener('download', (event: DownloadEvent) =>
        subscriber.next({ type: 'progress', progress: (event.percent ?? 0) / 100 }),
      )
        .then(listener => {
          removeListener = () => void listener.remove();
          return CapacitorUpdater.download({
            version: update.version,
            url: update.url,
            checksum: update.checksum,
            sessionKey: update.sessionKey,
          });
        })
        .then(bundle => {
          this.downloadedBundle = bundle;
          subscriber.next({ type: 'complete', success: true });
          subscriber.complete();
        })
        .catch(error => subscriber.error(error))
        .finally(() => removeListener?.());
    });
  }

  extractLiveUpdate(): Observable<UpdateEvents> {
    if (!Capacitor.isNativePlatform()) {
      return mockDownload();
    }
    return of<UpdateCompleteEvent>({ type: 'complete', success: true });
  }
}
